/**
 * The post-incident review, as a document — Step 54.
 *
 * Kai: *"it should be exportable as a PDF to send to stakeholders"*, then
 * *"the print export to pdf looks awful"*, then two real exports from his own
 * tenant so the target stopped being a guess. This is built against those —
 * the same blocks, in the same order, saying the same things:
 *
 *   Incident details     the facts, as a labelled grid
 *   Incident response    milestones, then MTTD / MTTA / MTTR / time in outage
 *   Incident summary     observed symptoms, root cause, impact, resolution
 *   Timeline entries     what happened, in order
 *   Action items         the follow-ups, with an owner and a status
 *
 * The PDF is the browser's own Save-as-PDF rather than a generated file: no
 * PDF engine as a dependency (`pdfjs-dist` is here to READ them), the tenant's
 * branding comes along because the document IS the page, and it prints on
 * paper — which is half of what "send it to stakeholders" turns out to mean.
 *
 * Action items are the incident's child TASK tickets. They are real tickets
 * with owners and statuses rather than rows in this document, and printing
 * them here is what makes that choice free instead of a trade-off.
 *
 * The print dialog is offered rather than fired on load: a page that opens one
 * before it has finished rendering prints a blank sheet.
 */
import { useEffect, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Printer } from 'lucide-react';
import {
  PIR_FACTS,
  PIR_SECTIONS,
  factValue,
  isHidden,
  ticketRef,
  type PirDocConfig,
  type TimelineEntry,
} from '@itsm/shared';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { apiFetch } from '../../lib/api';
import { ticketsApi } from '../../features/tickets/api';
import { qk } from '../../lib/queryClient';
import { fullTime } from '../../lib/time';
import { useBranding } from '../../lib/useBranding';
import type { MajorIncident } from '../../features/tickets/OutageTab';
import { derivePirFacts } from '../../features/tickets/pirFacts';

interface Pir {
  summary: string | null;
  rootCause: string | null;
  impact: string | null;
  resolution: string | null;
  wentWell: string | null;
  wentBadly: string | null;
  status: string;
  publishedAt: number | null;
  updatedAt: number | null;
  timelineEntries: TimelineEntry[] | null;
  docConfig: PirDocConfig | null;
}

interface ChildRow {
  id: string;
  subject: string;
  status: string | null;
  assigneeName?: string | null;
}

const clock = (ms: number) =>
  new Date(ms).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

export function ReviewPrintPage() {
  const { id = '' } = useParams();
  const branding = useBranding();

  const pir = useQuery({
    queryKey: qk.tickets.pir(id),
    queryFn: () => apiFetch<{ entry: Pir | null; suggestions: TimelineEntry[] }>(`/api/tickets/${id}/pir`),
    enabled: !!id,
  });
  const mi = useQuery({
    queryKey: qk.tickets.majorIncident(id),
    queryFn: () =>
      apiFetch<{ entry: (MajorIncident & { managerName?: string | null }) | null }>(
        `/api/tickets/${id}/major-incident`,
      ),
    enabled: !!id,
  });
  // `ticketsApi.detail`, not a hand-rolled fetch: the payload is
  // `{ ticket, slaClocks, … }`, and reading it as flat shipped a document with
  // a blank title — the sort of thing nobody notices until it has been
  // emailed to a board.
  const ticket = useQuery({
    queryKey: qk.tickets.detail(id),
    queryFn: () => ticketsApi.detail(id),
    enabled: !!id,
  });
  const children = useQuery({
    queryKey: qk.tickets.children(id),
    queryFn: () => apiFetch<{ entries: ChildRow[] }>(`/api/tickets/${id}/children`),
    enabled: !!id,
  });

  // A print stylesheet, injected here rather than in index.css: nothing else
  // in the app prints, and a global @media print block would be a rule
  // everyone else has to work around.
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @media print {
        .no-print { display: none !important; }
        /* Always on white, whatever theme the viewer is using. A dark-mode
           PDF is unreadable on paper and empties a cartridge. */
        html, body { background: #fff !important; }
        .doc, .doc * { color: #000 !important; border-color: #bbb !important; background: transparent !important; }
        .doc { max-width: none !important; padding: 0 !important; }
        .doc section, .doc tr { break-inside: avoid; }
        .doc h2 { break-after: avoid; }
      }
      @page { margin: 16mm; }
    `;
    document.head.appendChild(style);
    return () => style.remove();
  }, []);

  if (pir.isPending || ticket.isPending) return <Skeleton className="m-8 h-96" />;

  const p = pir.data?.entry;
  const m = mi.data?.entry ?? null;
  const t = ticket.data?.ticket as
    | (NonNullable<typeof ticket.data>['ticket'] & { resolvedAt?: number | null; closedAt?: number | null })
    | undefined;
  const clocks = ticket.data?.slaClocks ?? [];
  const entries = p?.timelineEntries ?? [];
  const actions = children.data?.entries ?? [];

  /**
   * Derived once, then filtered through the author's corrections.
   *
   * `derivePirFacts` is the SAME function the Review tab's editor calls, so
   * the "derived" column somebody read while correcting this is exactly what
   * would have printed. Two derivations would drift silently.
   */
  const cfg = p?.docConfig ?? null;
  const derived = derivePirFacts({ ticket: t as never, mi: m, slaClocks: clocks });
  const shown = (block: 'details' | 'response') =>
    PIR_FACTS.filter((f) => f.block === block && !isHidden(cfg, f.key));

  return (
    // Full bleed on purpose: a document, not a page in the app. The toolbar
    // spans the window and the article pads itself, because that padding IS
    // the printed margin — `@page` takes over once printing starts.
    <div className="min-h-screen bg-background">
      <div className="no-print sticky top-0 z-10 flex items-center gap-2 border-b bg-card px-4 py-2">
        <p className="text-sm text-muted-foreground">Print, or save as PDF from the print dialog.</p>
        <div className="flex-1" />
        <Button size="sm" onClick={() => window.print()}>
          <Printer className="h-4 w-4" /> Print
        </Button>
      </div>

      <article className="doc mx-auto max-w-3xl space-y-6 p-8 text-[13px] leading-relaxed">
        <header className="border-b pb-3">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
            {branding?.name ?? ''} · Post-incident review
          </p>
          <h1 className="mt-0.5 text-lg font-semibold">{t?.subject}</h1>
          <p className="text-xs text-muted-foreground">
            {t ? ticketRef(t.type, t.number) : ''}
            {p?.status === 'published' && p.publishedAt
              ? ` · Published ${fullTime(p.publishedAt)}`
              : ' · Draft — not yet published'}
            {/* A revision is a different fact from a publication, and the
                reader of a quoted document needs both. */}
            {p?.status === 'published' && p.updatedAt && p.publishedAt && p.updatedAt > p.publishedAt
              ? ` · Revised ${fullTime(p.updatedAt)}`
              : ''}
          </p>
        </header>

        {!isHidden(cfg, 'details') && (
          <Block title="Incident details">
            <dl className="grid grid-cols-2 gap-x-8 gap-y-2">
              {shown('details').map((f) => (
                <Fact key={f.key} label={f.label} value={factValue(cfg, f.key, derived[f.key] ?? '—')} />
              ))}
            </dl>
            <div className="mt-2 space-y-2">
              <Prose label="Impacted locations" html={m?.impactedLocations} />
              <Prose label="Impacted services" html={m?.impactedServices} />
              <Prose label="Business impact" html={m?.businessImpact} />
            </div>
          </Block>
        )}

        {!isHidden(cfg, 'response') && (
          <Block title="Incident response">
            {/* Milestones, then the metrics — the reference export's own
                split, and the one people read in that order. */}
            <dl className="grid grid-cols-2 gap-x-8 gap-y-2 sm:grid-cols-3">
              {shown('response')
                .filter((f) => f.key.endsWith('At'))
                .map((f) => (
                  <Fact key={f.key} label={f.label} value={factValue(cfg, f.key, derived[f.key] ?? '—')} />
                ))}
            </dl>
            {shown('response').some((f) => !f.key.endsWith('At')) && (
              <dl className="mt-3 grid grid-cols-2 gap-x-8 gap-y-2 border-t pt-3 sm:grid-cols-4">
                {shown('response')
                  .filter((f) => !f.key.endsWith('At'))
                  .map((f) => (
                    <Fact key={f.key} label={f.label} value={factValue(cfg, f.key, derived[f.key] ?? '—')} />
                  ))}
              </dl>
            )}
          </Block>
        )}

        {!isHidden(cfg, 'summary') && (
          <Block title="Incident summary">
            <div className="space-y-3">
              {PIR_SECTIONS.map((s) => {
                const html = (p?.[s.key as keyof Pir] as string | null) ?? '';
                // An empty optional section is omitted rather than printed as a
                // heading over whitespace — a stakeholder reading "What went
                // well" followed by nothing draws a conclusion.
                if (!html.trim()) return null;
                return (
                  <div key={s.key}>
                    <h3 className="text-[13px] font-semibold">{s.label}</h3>
                    {/* no-overflow on purpose: this is the PRINT view, and
                        paper has no viewport to protect. Clipping here would
                        cut a wide table off the page instead of scrolling it. */}
                    <div
                      className="prose prose-sm max-w-none dark:prose-invert"
                      // Sanitised on WRITE, never on render — the standing rule.
                      dangerouslySetInnerHTML={{ __html: html }}
                    />
                  </div>
                );
              })}
              {!p && <p className="text-muted-foreground">No review has been written yet.</p>}
            </div>
          </Block>
        )}

        {entries.length > 0 && !isHidden(cfg, 'timeline') && (
          <Block title="Timeline entries">
            <table className="w-full border-collapse">
              <tbody>
                {entries.map((e, i) => (
                  <tr key={i} className="border-b last:border-b-0">
                    <td className="w-24 whitespace-nowrap py-1 pr-3 align-top font-mono text-xs">
                      {clock(e.at)}
                    </td>
                    <td className="py-1 align-top">{e.text}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Block>
        )}

        {actions.length > 0 && !isHidden(cfg, 'actions') && (
          <Block title="Action items">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="py-1 pr-3 font-medium">Action</th>
                  <th className="w-40 py-1 pr-3 font-medium">Owner</th>
                  <th className="w-28 py-1 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {actions.map((a) => (
                  <tr key={a.id} className="border-b last:border-b-0">
                    <td className="py-1 pr-3 align-top">{a.subject}</td>
                    <td className="py-1 pr-3 align-top">{a.assigneeName ?? 'Unassigned'}</td>
                    <td className="py-1 align-top">{a.status ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Block>
        )}
      </article>
    </div>
  );
}

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h2 className="mb-2 border-b pb-1 text-sm font-semibold">{title}</h2>
      {children}
    </section>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}

/** A rich-text fact. Omitted entirely when empty, for the same reason the
 *  optional summary sections are. */
function Prose({ label, html }: { label: string; html: string | null | undefined }) {
  if (!html?.trim()) return null;
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      {/* no-overflow on purpose — print view, see above. */}
      <div
        className="prose prose-sm max-w-none dark:prose-invert"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
}
